import React, { createContext, useEffect, useMemo } from 'react';
import { useLocalStorage } from 'usehooks-ts';

import i18n from '@/i18n/config';

import { STORAGE_KEYS } from '../constants/storageKeys';

// Get browser language or default to 'en'
const getBrowserLanguage = (): string => {
  if (typeof window === 'undefined') return 'en';
  const browserLanguage = navigator.language || navigator.languages?.[0] || 'en';
  const languageCode = browserLanguage.split('-')[0].toLowerCase();
  return languageCode === 'es' ? 'es' : 'en';
};

interface AppProviderProps {
  children: React.ReactNode;
}

interface AppContextType {
  darkMode: boolean;
  setDarkMode: React.Dispatch<React.SetStateAction<boolean>>;
  language: string;
  setLanguage: React.Dispatch<React.SetStateAction<string>>;
}

export const AppContext = createContext<AppContextType>({} as AppContextType);

export default function AppProvider({ children }: Readonly<AppProviderProps>) {
  const [darkMode, setDarkMode] = useLocalStorage(`app-${STORAGE_KEYS.DARK_MODE}`, false);
  const [language, setLanguage] = useLocalStorage(`app-${STORAGE_KEYS.LANGUAGE}`, getBrowserLanguage());

  useEffect(() => {
    i18n.changeLanguage(language);
  }, [language]);

  const value = useMemo(
    () => ({
      darkMode,
      setDarkMode,
      language,
      setLanguage,
    }),
    [darkMode, setDarkMode, language, setLanguage]
  );

  return <AppContext.Provider value={value}>{children}</AppContext.Provider>;
}
